import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import {
  HelpCircle,
  Mail,
  Calendar,
  MessageCircle,
  Clock,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

export default function HelpPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Help & FAQ | Prozpector"
        description="Got questions about our done-for-you outbound service? Find answers here or get in touch directly."
        url="https://prozpector.com/help"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 md:py-32">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Questions? I've Got Answers.
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto">
              Everything you need to know about how the service works.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Get in Touch</h2>
            <p className="text-xl text-gray-600">Prefer to talk directly? Here's how to reach me.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <Card className="p-8 text-center">
              <div className="h-14 w-14 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Book a Call</h3>
              <p className="text-gray-600 mb-6">
                15 minutes to discuss your outbound needs and see if we're a good fit.
              </p>
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button className="w-full">
                  Schedule a Call <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>

            <Card className="p-8 text-center">
              <div className="h-14 w-14 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Send an Email</h3>
              <p className="text-gray-600 mb-6">
                Have a quick question? Drop me a line and I'll get back to you within 24 hours.
              </p>
              <a href="mailto:scott@prozpector.com">
                <Button variant="outline" className="w-full">
                  scott@prozpector.com
                </Button>
              </a>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <HelpCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">How does the service work?</h3>
              <p className="text-gray-600">
                We handle everything: prospect research, list building, email copywriting, and daily outreach. You approve the messaging before anything goes out, then we run campaigns and book meetings directly on your calendar. You just show up and close.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">What's included in the service?</h3>
              <p className="text-gray-600">
                Target audience research, custom prospect lists, personalized email copy, multi-step outreach sequences, daily campaign management, reply handling, and weekly performance reports. Everything you need to fill your pipeline without lifting a finger.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">How long until I see results?</h3>
              <p className="text-gray-600">
                Most clients see their first qualified meetings within the first 2-3 weeks. Outbound takes time to warm up—we typically reach full velocity by week 4-6 as we optimize targeting and messaging based on real responses.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">What industries do you work with?</h3>
              <p className="text-gray-600">
                I work best with B2B companies selling to other businesses—SaaS, agencies, consultants, and professional services. If you have a clear ideal customer profile and sell something that requires a conversation to close, we should talk.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">Do I need to provide anything?</h3>
              <p className="text-gray-600">
                Just your time for an initial discovery call to understand your business, ideal customer, and what's worked before. I'll handle everything else. You'll review and approve messaging before campaigns go live.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">What if I'm not happy with the results?</h3>
              <p className="text-gray-600">
                If we don't deliver the agreed number of qualified meetings, you don't pay. Simple as that. I'm confident in the system because I use it myself—but I also want you to feel protected.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">How is this different from hiring an SDR?</h3>
              <p className="text-gray-600">
                An SDR costs $50-80K+ per year in salary alone, plus training, tools, and management time. And they might quit. With Prozpector, you get a proven system from day one, no hiring risk, and results you can count on.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-2">Can I pause or cancel?</h3>
              <p className="text-gray-600">
                Yes. This is a month-to-month engagement. If you need to pause or stop, just let me know with 2 weeks notice. No long-term contracts, no cancellation fees.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What to Expect</h2>
            <p className="text-xl text-gray-600">Here's how we'll work together</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex gap-6 items-start">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Quick Onboarding</h3>
                <p className="text-gray-600">
                  One 30-minute call to understand your business, then I build your system. You'll be reviewing copy within a week.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Regular Communication</h3>
                <p className="text-gray-600">
                  Weekly reports on campaign performance. Direct access to me via email for any questions or adjustments.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Full Transparency</h3>
                <p className="text-gray-600">
                  You'll see exactly what's being sent, to whom, and what results we're getting. No black boxes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto bg-black text-white rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Talk?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss your outbound goals and see if we're a good fit.
            </p>
            <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="bg-white text-black hover:bg-gray-100 border-white text-lg px-8 py-6">
                <Calendar className="mr-2 h-5 w-5" />
                Book a Discovery Call
              </Button>
            </a>
            <p className="text-sm text-gray-400 mt-6">
              15 minutes. No pressure. Just a conversation.
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
